import type { TextareaHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';
import {
  controlBorderClass,
  controlDisabled,
  controlFocus,
  controlHover,
  controlLoading,
  errorMessageId,
} from '../primitives/shared.js';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  loading?: boolean;
}

/** PRM-04 Textarea — multi-line entry. Not permitted in `floor`. */
export function Textarea({
  label,
  error,
  loading,
  id,
  className,
  disabled,
  rows = 4,
  'aria-describedby': ariaDescribedBy,
  ...props
}: TextareaProps) {
  const fieldId = id ?? props.name;
  const errorId = errorMessageId(fieldId);
  const describedBy =
    [ariaDescribedBy, error ? errorId : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-field-label [[data-theme=floor]_&]:hidden">
      {label ? (
        <label htmlFor={fieldId} className="text-label font-medium text-default">
          {label}
        </label>
      ) : null}
      <textarea
        id={fieldId}
        rows={rows}
        data-primitive="PRM-04"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        aria-disabled={disabled || undefined}
        aria-busy={loading || undefined}
        readOnly={disabled}
        className={cn(
          'w-full rounded-control bg-raised p-field text-body text-default placeholder:text-placeholder',
          controlBorderClass(error),
          controlHover,
          controlFocus,
          controlDisabled,
          controlLoading,
          className,
        )}
        {...props}
      />
      {error ? (
        <span id={errorId} role="alert" className="text-body-sm text-danger">
          {error}
        </span>
      ) : null}
    </div>
  );
}
